import { Link, NavLink, useNavigate } from "react-router-dom";
import { BookOpenText, Compass, Map as MapIcon, Sparkles, HelpCircle, Bookmark, Search, Hourglass, Sparkle, Moon } from "lucide-react";
import { useState, useEffect, useRef } from "react";

const links = [
  { to: "/livre-du-soir", label: "Livre du soir", icon: Moon },
  { to: "/voyage", label: "Voyage", icon: Hourglass },
  { to: "/frise", label: "Frise", icon: Compass },
  { to: "/carte", label: "Carte", icon: MapIcon },
  { to: "/origine", label: "À l'origine", icon: Sparkle },
  { to: "/etrange", label: "Étrange", icon: Sparkles },
  { to: "/quiz", label: "Vrai ou légende ?", icon: HelpCircle },
];

export default function Navbar() {
  const header = useRef(null);
  useEffect(() => {
    const measure = () => document.documentElement.style.setProperty("--chroniques-header-height", `${header.current?.getBoundingClientRect().height || 148}px`);
    const observer = new ResizeObserver(measure);
    observer.observe(header.current);
    measure();
    return () => {
      observer.disconnect();
      document.documentElement.style.removeProperty("--chroniques-header-height");
    };
  }, []);
  const [q, setQ] = useState("");
  const navigate = useNavigate();

  const submit = (e) => {
    e.preventDefault();
    if (q.trim()) navigate(`/recherche?q=${encodeURIComponent(q.trim())}`);
  };

  return (
    <header ref={header} className="fixed top-0 left-0 right-0 z-50 border-b border-copper/20 backdrop-blur-xl bg-[#050814]/80" data-testid="navbar">
      <style>{`
        .chroniques-biography-button { display:inline-flex; align-items:center; justify-content:center; min-height:52px; padding:10px 24px; border:2px solid #F3D98E; border-radius:10px; background:#D4B978; color:#050814; font-size:clamp(18px,2vw,22px); font-weight:800; line-height:1.25; text-decoration:none; animation:chroniquesBiographyBlink 2.4s ease-in-out infinite; }
        .chroniques-biography-button:hover { background:#F3D98E; }
        .chroniques-biography-button:hover, .chroniques-biography-button:focus-visible { animation:none; }
        .chroniques-biography-button:focus-visible { outline:3px solid #F3E9D2; outline-offset:4px; }
        @keyframes chroniquesBiographyBlink { 0%,100% { opacity:1; box-shadow:0 0 5px rgba(212,185,120,.25); } 50% { opacity:.72; box-shadow:0 0 24px rgba(243,217,142,.8); } }
        @media (prefers-reduced-motion:reduce) { .chroniques-biography-button { animation:none; } }
      
        .App > main { padding-top:var(--chroniques-header-height,148px); }
      `}</style>
      <div className="mx-auto max-w-7xl px-6 lg:px-10 py-2 border-b border-copper/20">
        <a href="https://biographie.dorotheelancelot.fr/" data-testid="nav-biographie" className="chroniques-biography-button">
          Qui suis-je ?
        </a>
      </div>
      <div className="mx-auto max-w-7xl px-6 lg:px-10 py-4 flex items-center gap-6">
        <Link to="/" className="flex items-center gap-3 group shrink-0" data-testid="logo-link">
          <BookOpenText className="w-6 h-6 text-copper group-hover:text-copper-light" strokeWidth={1.5} />
          <div className="hidden sm:block leading-tight">
            <div className="font-heading text-xl tracking-tight text-parchment">Les Chroniques</div>
            <div className="overline">de l'Étrange</div>
          </div>
        </Link>

        <nav className="hidden lg:flex items-center gap-1 ml-2">
          {links.map(({ to, label, icon: Icon }) => (
            <NavLink
              key={to}
              to={to}
              data-testid={`nav-${to.slice(1)}`}
              className={({ isActive }) =>
                `flex items-center gap-1.5 px-2.5 py-2 text-[0.82rem] font-ui rounded-sm transition-colors duration-300 whitespace-nowrap ${
                  isActive ? "text-copper-light" : "text-parchment/70 hover:text-copper-light"
                }`
              }
            >
              <Icon className="w-3.5 h-3.5" strokeWidth={1.5} />
              {label}
            </NavLink>
          ))}
        </nav>

        <form onSubmit={submit} className="ml-auto flex items-center gap-2 flex-1 max-w-xs">
          <div className="relative w-full">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-copper-muted" strokeWidth={1.5} />
            <input
              data-testid="search-input"
              value={q}
              onChange={(e) => setQ(e.target.value)}
              placeholder="Chercher un récit…"
              className="w-full pl-9 pr-3 py-2 bg-[#0F1528] border border-copper/20 rounded-sm text-sm font-ui text-parchment placeholder:text-copper-muted/60 focus:outline-none focus:border-copper/60"
            />
          </div>
        </form>

        <NavLink to="/bibliotheque" data-testid="nav-favoris" className="flex items-center gap-2 px-3 py-2 text-sm font-ui text-parchment/80 hover:text-copper-light">
          <Bookmark className="w-4 h-4" strokeWidth={1.5} />
          <span className="hidden md:inline">Ma bibliothèque</span>
        </NavLink>
      </div>
    </header>
  );
}
